'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Menu, MenuItem } from '@/utils/drupal-api/__generated__/graphql';
import { Dialog, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import { BrandConfig } from 'types/brand-config';

import MenuGroup from '@/components/ui/menu';

const MenuPopover: React.FC<{
  menu: Menu;
  drawer?: boolean;
  brand: BrandConfig;
}> = ({ menu, drawer }) => {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState<number | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const resizeEvent = () => {
      const head = document.getElementById('menu-bar-header');
      if (head) {
        setOffset(head.clientHeight);
      }
    };
    // initial offset
    resizeEvent();
    window.addEventListener('resize', resizeEvent);
    return () => {
      window.removeEventListener('resize', resizeEvent);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <button
        type="button"
        className="w-10 p-0"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <div className="flex flex-col items-center justify-center text-primary-main">
          {!open && (
            <svg
              className="h-7 w-7"
              viewBox="0 0 38 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="38" height="3" rx="1.5" fill="currentColor" />
              <rect
                y="10.5"
                width="38"
                height="3"
                rx="1.5"
                fill="currentColor"
              />
              <rect y="21" width="38" height="3" rx="1.5" fill="currentColor" />
            </svg>
          )}
          {open && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 p-1"
              viewBox="0 0 28 28"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.664018 0.591855C-0.125122 1.381 -0.125121 2.66045 0.664019 3.44959L11.1424 13.928L0.663801 24.4066C-0.12534 25.1957 -0.12534 26.4751 0.6638 27.2643C1.45294 28.0534 2.73239 28.0534 3.52153 27.2643L14.0001 16.7857L24.4784 27.264C25.2676 28.0531 26.547 28.0531 27.3362 27.264C28.1253 26.4749 28.1253 25.1954 27.3362 24.4063L16.8579 13.928L27.336 3.44987C28.1251 2.66073 28.1251 1.38128 27.336 0.592136C26.5468 -0.197005 25.2674 -0.197004 24.4782 0.592137L14.0001 11.0702L3.52175 0.591856C2.73261 -0.197285 1.45316 -0.197285 0.664018 0.591855Z"
                fill="currentColor"
              />
            </svg>
          )}
          <span className="whitespace-nowrap text-sm font-semibold uppercase tracking-[0.05em] text-inherit xl:text-[15px]">
            {!open ? 'Menu' : 'Close'}
          </span>
          {/* <span className="ut-xxs block whitespace-nowrap text-inherit sm:hidden lg:capitalize">
            {!open ? 'Menu' : 'Close'}
          </span> */}
        </div>
      </button>
      {open && (
        <Dialog open={open} onClose={setOpen} className="relative">
          <div
            style={{ top: `${offset || 0}px` }}
            className={clsx(
              `pointer-events-none fixed bottom-0 left-0 right-0 z-30 bg-white`,
              drawer && 'lg:right-0 lg:max-w-sm'
            )}
          >
            <div className={clsx(`mx-auto w-full max-w-screen-2xl`)}>
              {/* Adjusted to allow scrolling */}
              <div className="pointer-events-auto flex-none">
                <DialogPanel
                  transition
                  className={`pointer-events-auto w-full transform border-t border-gray-4 px-[1rem] transition duration-500 ease-in-out data-[closed]:translate-y-0 sm:duration-700`}
                >
                  <div
                    style={{
                      maxHeight: `calc(100vh - ${offset ? offset + 16 : 0}px)`,
                    }}
                    className={`no-scrollbar h-screen overflow-y-scroll`}
                  >
                    <div className="flex h-full w-full flex-col">
                      {menu.items && (
                        <MenuGroup
                          drawer={drawer}
                          items={menu.items as MenuItem[]}
                        />
                      )}
                    </div>
                  </div>
                </DialogPanel>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default MenuPopover;
